/**
 * Envoie au serveur ce que le classement compte : les boucliers et la confiance
 * gagnés par mission, et les chutes (game over). Envois sans attente, comme
 * recordPedaEvent : le jeu ne doit jamais bloquer dessus.
 */
import { apiFetch } from './api-client';
import {
  CAMPAIGN_KEY_PREFIX as PEDA_CAMPAIGN_KEY_PREFIX,
  loadPedaCampaignStore,
  sumPedaCategoryCounts,
  type PedaCategoryCounts,
} from './peda-shield-stats';
import {
  CAMPAIGN_KEY_PREFIX as TRUST_CAMPAIGN_KEY_PREFIX,
  loadTrustCampaignStore,
  type TrustPointsByCharacter,
} from './ally-trust';

export interface MissionResultPayload {
  mission_id: string;
  peda_shields: number;
  trust: TrustPointsByCharacter;
}

const LOCAL_SYNC_SESSION_KEY = 'vn_campaign_results_synced';
/** Les sauvegardes locales ne sont pas rangées par compte : on les attribue au premier qui les envoie. */
const LOCAL_SYNC_OWNER_KEY = 'vn_campaign_results_owner';

function postJson(url: string, body: unknown): void {
  void apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => {
    // Fire-and-forget: le classement ne doit pas gêner la partie.
  });
}

function sendMissionResults(scenarioId: string, results: MissionResultPayload[]): void {
  if (results.length === 0) return;
  postJson('/api/stats/mission-results', { scenario_id: scenarioId, results });
}

/** Fin de mission ou changement de mission : le serveur garde le meilleur score. */
export function reportMissionResult(options: {
  scenarioId: string;
  missionId: string;
  liveCounts: Partial<PedaCategoryCounts>;
  liveTrust: TrustPointsByCharacter;
}): void {
  const { scenarioId, missionId, liveCounts, liveTrust } = options;
  sendMissionResults(scenarioId, [
    { mission_id: missionId, peda_shields: sumPedaCategoryCounts(liveCounts), trust: { ...liveTrust } },
  ]);
}

/**
 * Une chute compte quand on arrive sur une scène gameOver en jouant, depuis une autre scène.
 * Une sauvegarde rouverte directement sur le game over (pas de scène précédente) ne compte pas.
 */
export function isNewGameOver(
  previousSceneId: string | null,
  scene: { id: string; type?: string } | null,
): boolean {
  return Boolean(scene && previousSceneId && previousSceneId !== scene.id && scene.type === 'gameOver');
}

export function reportGameOver(options: {
  scenarioId: string;
  missionId?: string | null;
  sceneId: string;
}): void {
  postJson('/api/stats/game-over', {
    scenario_id: options.scenarioId,
    mission_id: options.missionId ?? null,
    scene_id: options.sceneId,
  });
}

function scenarioIdsWithPrefix(storage: Storage, prefix: string): string[] {
  const ids: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key?.startsWith(prefix) && key.length > prefix.length) ids.push(key.slice(prefix.length));
  }
  return ids;
}

/** Les résultats par mission déjà gardés dans ce navigateur, scénario par scénario. */
export function collectLocalCampaignResults(
  storage: Storage = localStorage,
): { scenarioId: string; results: MissionResultPayload[] }[] {
  const scenarioIds = new Set([
    ...scenarioIdsWithPrefix(storage, PEDA_CAMPAIGN_KEY_PREFIX),
    ...scenarioIdsWithPrefix(storage, TRUST_CAMPAIGN_KEY_PREFIX),
  ]);

  const collected: { scenarioId: string; results: MissionResultPayload[] }[] = [];
  for (const scenarioId of scenarioIds) {
    const peda = loadPedaCampaignStore(scenarioId).byMission;
    const trust = loadTrustCampaignStore(scenarioId).byMission;
    const missionIds = new Set([...Object.keys(peda), ...Object.keys(trust)]);
    const results = [...missionIds].map((missionId) => ({
      mission_id: missionId,
      peda_shields: sumPedaCategoryCounts(peda[missionId] ?? {}),
      trust: { ...(trust[missionId] ?? {}) },
    }));
    if (results.length > 0) collected.push({ scenarioId, results });
  }
  return collected;
}

/**
 * Reprise de l'historique : avant le classement, boucliers et confiance n'étaient
 * gardés que dans le navigateur. Envoyé une fois par session, et seulement pour le
 * compte qui a envoyé ces sauvegardes la première fois (navigateur partagé).
 */
export function syncLocalCampaignResults(userId: string): void {
  try {
    if (sessionStorage.getItem(LOCAL_SYNC_SESSION_KEY) === userId) return;
    sessionStorage.setItem(LOCAL_SYNC_SESSION_KEY, userId);

    const owner = localStorage.getItem(LOCAL_SYNC_OWNER_KEY);
    if (owner && owner !== userId) return;
    const pending = collectLocalCampaignResults();
    if (pending.length === 0) return;
    localStorage.setItem(LOCAL_SYNC_OWNER_KEY, userId);
    for (const { scenarioId, results } of pending) {
      sendMissionResults(scenarioId, results);
    }
  } catch {
    // Stockage indisponible (navigation privée stricte) : rien à reprendre.
  }
}
